// Constants
import { CustomDateRangeFilterPrefix } from "../constants/FilterConstants";
// Models
import { EntityFilter, Filter } from "../client/filter/Filter";
// Utilities
import { formatShortDate } from "@ms/uno-utilities/lib/local/DateUtilities-temporal";
import { Temporal } from "temporal-polyfill-lite";
/**
 * Whether a filter value encodes a custom date range.
 * @param value A value from a date filter
 */ export function isCustomDateRangeFilterValue(value) {
    return typeof value === "string" && value.startsWith(CustomDateRangeFilterPrefix);
}
/**
 * Encodes a range as a single filter value, e.g. "customRange:2026-01-01:2026-01-31", or "customRange:2026-01-01:" with no end.
 * @param range The range to encode
 */ export function toCustomDateRangeFilterValue(range) {
    return `${CustomDateRangeFilterPrefix}${range.start?.toString() ?? ""}:${range.end?.toString() ?? ""}`;
}
/**
 * Parses an encoded range.
 * @param value A value from a date filter
 * @returns The range, or null when the value is not a well-formed range (e.g. a corrupted cached filter)
 */ export function parseCustomDateRangeFilterValue(value) {
    if (typeof value !== "string" || !isCustomDateRangeFilterValue(value)) {
        return null;
    }
    const [start, end, ...rest] = value.slice(CustomDateRangeFilterPrefix.length).split(":");
    if (end == null || rest.length > 0) {
        return null;
    }
    // An empty bound is open (undefined); a malformed one (null) invalidates the range.
    const startDate = start === "" ? undefined : parseIsoDate(start);
    const endDate = end === "" ? undefined : parseIsoDate(end);
    if (startDate === null || endDate === null) {
        return null;
    }
    const range = {
        start: startDate,
        end: endDate
    };
    return hasBound(range) ? range : null;
}
/**
 * Whether a local date falls within an encoded inclusive range.
 * @param date The task's local date, or null when the task has no date
 * @param value An encoded range
 */ export function isInCustomDateRange(date, value) {
    if (date == null || !isCustomDateRangeFilterValue(value)) {
        return false;
    }
    // ISO calendar dates order lexicographically, which avoids parsing the range for every task.
    const [start, end] = value.slice(CustomDateRangeFilterPrefix.length).split(":");
    const isoDate = date.toString();
    return (start === "" || start <= isoDate) && (end === "" || isoDate <= end);
}
/**
 * Gets the custom range applied to a date field.
 * @param taskFilter The current task filter
 * @param filterField The date filter field
 * @returns The applied range, or null when none is applied
 */ export function getCustomDateRangeForFilterField(taskFilter, filterField) {
    const values = taskFilter.filters.find((filter)=>filter.field === filterField)?.values ?? [];
    return parseCustomDateRangeFilterValue(values.find(isCustomDateRangeFilterValue) ?? null);
}
/**
 * Replaces the custom range on a date field, keeping its other selected values.
 * @param taskFilter The current task filter
 * @param filterField The date filter field
 * @param range The new range, or null to remove the field's range
 */ export function setCustomDateRange(taskFilter, filterField, range) {
    const existingFilter = taskFilter.filters.find((filter)=>filter.field === filterField);
    const values = (existingFilter?.values ?? []).filter((value)=>!isCustomDateRangeFilterValue(value));
    if (range != null) {
        values.push(toCustomDateRangeFilterValue(range));
    }
    return values.length > 0 ? taskFilter.replaceFilter(filterField, new Filter(filterField, values, existingFilter?.label, existingFilter?.active)) : taskFilter.removeFilter(filterField);
}
/**
 * Whether any field has a custom range applied.
 * @param taskFilter The task filter to inspect
 */ export function hasCustomDateRange(taskFilter) {
    return taskFilter.filters.some((filter)=>filter.values.some(isCustomDateRangeFilterValue));
}
/**
 * Removes custom ranges from every field, dropping fields left without values.
 * @param taskFilter The task filter to strip
 */ export function removeCustomDateRanges(taskFilter) {
    const filters = taskFilter.filters.flatMap((filter)=>{
        const values = filter.values.filter((value)=>!isCustomDateRangeFilterValue(value));
        if (values.length === filter.values.length) {
            return [
                filter
            ];
        }
        return values.length > 0 ? [
            new Filter(filter.field, values, filter.label, filter.active)
        ] : [];
    });
    return new EntityFilter(filters);
}
/**
 * Formats a range for display using the locale's short date, e.g. "6/2/27 – 6/13/27", "6/2/27" for a single day,
 * or "6/2/27 –" with no end.
 * @param range The range to format
 */ export function formatCustomDateRange(range) {
    const start = range.start != null ? formatShortDate(range.start.toString()) : "";
    const end = range.end != null ? formatShortDate(range.end.toString()) : "";
    return start === end ? start : `${start} \u2013 ${end}`.trim();
}
/** Whether a range has at least one bound. */ function hasBound(range) {
    return range.start != null || range.end != null;
}
/** Parses a canonical ISO calendar date, rejecting malformed or impossible dates such as 2026-02-31. */ function parseIsoDate(isoDate) {
    try {
        const date = Temporal.PlainDate.from(isoDate);
        return date.toString() === isoDate ? date : null;
    } catch  {
        return null;
    }
}
